/**
 * Translate Luma's suggested filters into search-page filters.
 *
 * Luma decorates every answer with a `filter` array of single-key objects —
 * `[{"County":"Chaves"},{"DocType":"Discharge Permit"}]` — produced by a
 * separate pass over the question and answer (`ResponseMetadataExtractor`).
 * It describes what the exchange was *about*; it is not a record of the
 * retrieval Luma performed.
 *
 * The search page addresses fields as `{group, code}` drawn from the document
 * schema (`CLASSIFICATION.DOCTYPE`, `LOCATION.COUNTY`, …). Luma writes the same
 * concepts in its own casing, so rather than maintain a lookup table that goes
 * stale every time the schema grows, we match both sides on a canonical form
 * and let the live schema be the authority.
 */

import type { DocumentFilter, DocumentSchemaField } from '@/types/measure-document-types';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Reduce a field name to letters and digits only, upper-cased.
 *
 * Collapses the two naming styles onto one key, including the cases where a
 * word boundary is present on one side but not the other:
 *
 *   FacilityName → FACILITYNAME  ←  LOCATION.FACILITY_NAME
 *   DocType      → DOCTYPE       ←  CLASSIFICATION.DOCTYPE
 *   EPA_ID       → EPAID         ←  IDENTIFIER.EPA_ID
 */
export function canonicalFieldKey(name: string): string {
   return name.replace(/[^a-z0-9]/gi, '').toUpperCase();
}

/**
 * Which group wins when one canonical key matches several schema fields.
 *
 * Real collisions exist — `APPLICATION` and `INSPECTION` are both a DATE and a
 * TAG. DATE is last because its filters take a `{start, end}` range while Luma
 * only ever supplies a scalar, so a date field can't service one of its
 * suggestions anyway.
 */
const GROUP_PRIORITY = [
   'CLASSIFICATION',
   'LOCATION',
   'IDENTIFIER',
   'PARTY',
   'METRIC',
   'TAG',
   'NARRATIVE',
   'ACCESS',
   'DATE',
];

function groupRank(group: string): number {
   const index = GROUP_PRIORITY.indexOf(group);
   return index === -1 ? GROUP_PRIORITY.length : index;
}

/* ---------------------------------------------------------------------------------------------- */

export interface MappedLumaFilters {
   /** Filters the search page can apply directly. */
   filters: DocumentFilter[];
   /** Keys that matched no usable schema field, for the caller to fall back on. */
   unmapped: string[];
}

/**
 * Resolve Luma's suggestions against the document schema.
 *
 * Anything the schema doesn't know — or knows only as a date range — is
 * reported in `unmapped` rather than guessed at, so the caller can degrade to
 * a keyword search instead of navigating to a filter the API would reject.
 */
export function mapLumaFiltersToDocumentFilters(
   lumaFilter: readonly Record<string, unknown>[],
   schema: readonly DocumentSchemaField[],
): MappedLumaFilters {
   // Best schema field per canonical key, chosen by group priority.
   const byKey = new Map<string, DocumentSchemaField>();
   for (const field of schema) {
      const key = canonicalFieldKey(field.code);
      const existing = byKey.get(key);
      if (!existing || groupRank(field.group) < groupRank(existing.group))
         byKey.set(key, field);
   }

   const filters: DocumentFilter[] = [];
   const unmapped: string[] = [];
   const seen = new Set<string>();

   for (const entry of lumaFilter ?? []) {
      for (const [name, rawValue] of Object.entries(entry ?? {})) {
         const value = normalizeValue(rawValue);
         if (value === null) {
            unmapped.push(name);
            continue;
         }

         const field = byKey.get(canonicalFieldKey(name));
         // A DATE field needs a range; Luma's scalar can't fill one.
         if (!field || field.group === 'DATE') {
            unmapped.push(name);
            continue;
         }

         // Luma repeats itself across turns; the search page treats a duplicate
         // group/code/value as a second active facet rather than a no-op.
         const dedupeKey = `${field.group}.${field.code}.${value}`;
         if (seen.has(dedupeKey))
            continue;
         seen.add(dedupeKey);

         filters.push({ group: field.group, code: field.code, value });
      }
   }

   return { filters, unmapped };
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * Keep only the filters whose value the index actually holds.
 *
 * Luma names fields well but invents values: it suggested
 * `DocType: "Discharge Permit"` for a groundwater-permit answer, where the
 * facet vocabulary offers `Permitting`, `Inspection Report`, `Compliance
 * Report` and so on. Filters are ANDed, so a single invented value takes the
 * result set to zero — which is how a correct-looking navigation lands on an
 * empty page.
 *
 * Matching is case- and whitespace-insensitive but not fuzzy: guessing that
 * "Discharge Permit" means "Permitting" would put words in the user's mouth.
 * A value the index doesn't have comes back in `rejected` for the caller to
 * search as a keyword instead.
 */
export function keepIndexedFilterValues(
   filters: readonly DocumentFilter[],
   valuesByField: ReadonlyMap<string, readonly string[]>,
): { kept: DocumentFilter[]; rejected: DocumentFilter[] } {
   const kept: DocumentFilter[] = [];
   const rejected: DocumentFilter[] = [];

   for (const filter of filters) {
      const field = `${filter.group}.${filter.code}`;
      const known = valuesByField.get(field);

      // No vocabulary for the field means the lookup failed, not that the value
      // is wrong — applying it optimistically beats silently dropping it.
      if (!known) {
         kept.push(filter);
         continue;
      }

      const match = known.find(v => looseEqual(v, String(filter.value)));
      if (match === undefined) {
         rejected.push(filter);
         continue;
      }

      // Adopt the index's own casing so the chip matches the facet list.
      kept.push({ ...filter, value: match });
   }

   return { kept, rejected };
}

function looseEqual(a: string, b: string): boolean {
   return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/* ---------------------------------------------------------------------------------------------- */

/** Scalars only — anything else can't address a facet. */
function normalizeValue(raw: unknown): string | number | null {
   if (typeof raw === 'number' && Number.isFinite(raw))
      return raw;
   if (raw instanceof Date)
      return raw.toISOString().slice(0, 10);
   if (typeof raw !== 'string')
      return null;
   const trimmed = raw.trim();
   return trimmed || null;
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * Every value Luma suggested, as a keyword string.
 *
 * The fallback for when nothing mapped: a full-text search for the same terms
 * is a worse search than a faceted one, but it is a great deal better than
 * sending the user to an empty results page.
 */
export function lumaFilterKeywords(lumaFilter: readonly Record<string, unknown>[]): string {
   const values: string[] = [];
   for (const entry of lumaFilter ?? []) {
      for (const raw of Object.values(entry ?? {})) {
         const value = normalizeValue(raw);
         if (value !== null && !values.includes(String(value)))
            values.push(String(value));
      }
   }
   return values.join(' ');
}
